"use client";

import Link from "next/link";

import { Consultation } from "@/app/_components/consultation-card";
import { Error } from "@/app/_components/error";
import { Button } from "@/components/ui/button";
import { Loading } from "../loading";
import { SearchLink } from "../search-link";
import { ExOwnerInfo } from "../ex-owner-info";

import { useAnalyze } from "@/queries/use-analyze";

import { banks } from "@/constants";

import type { DiligenceItem } from "../diligence.types";

const urls = {
  google: "https://www.google.com/search?q={query}",
};

const createSearchUrl = (baseUrl: string, query: string) => {
  return baseUrl.replace("{query}", encodeURIComponent(query || ""));
};

export const Dividas = ({ id, field, emptyElement }: DiligenceItem) => {
  const { data, isPending, error, refetch } = useAnalyze({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const hasValidData = data?.analise?.data && data?.propriedade;

  if (!hasValidData) {
    return emptyElement ?? <p>Não há dados válidos para exibir.</p>;
  }

  const analyzeData = data?.analise?.data;
  const property = data?.propriedade;

  const currentOwner =
    analyzeData.moradores && analyzeData.moradores.length > 0
      ? analyzeData.moradores[0]
      : null;

  const uf = property?.uf || "";
  const city = property.cidade_descricao || "";
  const endereco = analyzeData.imovel?.endereco;
  const inscricaoImobiliaria = property?.inscricao_imobiliaria || "";

  const iptuSearchUrl = createSearchUrl(
    urls.google,
    `IPTU ${city} ${uf} consulta online`
  );

  const condominioSearchUrl = createSearchUrl(
    urls.google,
    `contato condominio ${endereco} ${city} ${uf}`
  );

  return (
    <div className="flex flex-col gap-8">
      <ExOwnerInfo
        currentOwner={currentOwner}
        analyzeData={analyzeData}
        propertyNumber={inscricaoImobiliaria}
      />
      <Consultation.Card
        title="IPTU"
        description="Se o arrematante for responsabilizado pelos débitos tributários, ele deve entrar no site da prefeitura do município e tentar acesso às guias de IPTU do imóvel relativas aos 5 últimos anos."
      >
        <SearchLink
          url={iptuSearchUrl}
          label={`Consultar IPTU em ${city}`}
          icon={"/logos/google-logo.svg"}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Condomínio"
        description="Se o arrematante for responsabilizado pelas dívidas condominiais, ele deve entrar em contato com a administração do condomínio ou com o síndico e solicitar os débitos da unidade."
      >
        <SearchLink
          url={condominioSearchUrl}
          label={`Consultar contato de ${endereco} em ${city}`}
          icon={"/logos/google-logo.svg"}
        />
        <div className="flex items-center gap-4">
          <div>
            <img src={banks.caixa.logo} alt="Caixa" className="w-10 h-10" />
          </div>
          <p className="text-sm text-muted-foreground">
            Alguns condomínios alegam não poder compartilhar essas informações.
            Nesses casos, apresente a autorização oficial da CAIXA para
            consultas de débitos condominiais.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <img
              src="/logos/jusbrasil-logo.svg"
              alt="Jusbrasil"
              className="w-10 h-10"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Uma outra estratégia é{" "}
            <Button
              asChild
              variant="link"
              size="sm"
              className="text-sm p-0 min-h-0 underline"
            >
              <Link
                prefetch={false}
                href={`/app/matriculas/${id}/diligencia/processos`}
              >
                buscar por processos contra o antigo proprietário
              </Link>
            </Button>{" "}
            ― se o condomínio já ajuizou a cobrança, pode-se encontrar a tabela
            de progressão da dívida nos autos.
          </p>
        </div>
      </Consultation.Card>
      <ul className="list-disc list-inside flex flex-col gap-2">
        <li className="text-sm text-muted-foreground">
          Seu imóvel está em área rural?{" "}
          <a
            href="https://solucoes.receita.fazenda.gov.br/Servicos/certidaointernet/ITR/EmitirPGFN"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Faça uma consulta do ITR.
          </a>
        </li>
        <li className="text-sm text-muted-foreground">
          Seu imóvel está em área de aforamento?{" "}
          <a
            href="https://www.gov.br/pt-br/servicos/calcular-laudemio"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Faça uma consulta do Laudêmio.
          </a>
        </li>
      </ul>
    </div>
  );
};
